// src/components/timeline/chart/useTimelineChartDate.tsx
import * as d3 from "d3";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween"; // Import isBetween if used
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import { useEffect, useMemo } from "react";
import { useToken } from "../../../hooks/useToken";

// Extend dayjs if not done globally elsewhere for these plugins
dayjs.extend(localeData);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween); // Include if needed for visibility checks

// Interface for tick data
interface TickData {
  x: Date;
  y: number;
  level: "year" | "month" | "day";
}

// Interface for weekend background data
interface WeekendRectData {
  id: string; // Unique ID for data binding
  startX: number;
  width: number;
  y: number;
  height: number;
}

export function useTimelineChartDate({
  svgRef,
  width,
  height,
  dateMax: dateMaxProp, // Rename prop to avoid conflict with internal Dayjs var
  dateMin: dateMinProp, // Rename prop to avoid conflict with internal Dayjs var
  _days,
}: {
  width: number;
  height: number;
  svgRef: React.RefObject<SVGSVGElement | null>;
  dateMin: Date; // Keep prop type as Date
  dateMax: Date; // Keep prop type as Date
  _days: number;
}) {
  const { token, screens } = useToken();

  // Use Dayjs objects internally for easier manipulation
  const dateMin = useMemo(() => dayjs(dateMinProp), [dateMinProp]);
  const dateMax = useMemo(() => dayjs(dateMaxProp), [dateMaxProp]);

  // --- Calculate Tick Data and Level ---
  const { tickData, level } = useMemo(() => {
    // Use internal Dayjs objects (dateMin, dateMax)
    if (!dateMin.isValid() || !dateMax.isValid() || width <= 0) {
      return { tickData: [], level: "month" as "year" | "month" | "day" };
    }

    const startDate = dateMin; // Use Dayjs object
    const endDate = dateMax; // Use Dayjs object
    const durationYears = endDate.diff(startDate, "year", true);
    const estimatedDays = _days;

    let currentLevel: "year" | "month" | "day" = "month";
    let tickInterval: dayjs.ManipulateType = "month";
    let startUnit: dayjs.ManipulateType = "month";

    if (estimatedDays <= 40) {
      currentLevel = "day";
      tickInterval = "day";
      startUnit = "day";
    } else if (durationYears > 1.5) {
      currentLevel = "year";
      tickInterval = "year";
      startUnit = "year";
    } else {
      currentLevel = "month";
      tickInterval = "month";
      startUnit = "month";
    }

    const ticks: TickData[] = [];
    // Start iteration from the beginning of the relevant unit of the start date
    let currentDate = startDate.startOf(startUnit);

    // Iterate while the *start* of the current interval is before the end date
    while (currentDate.isBefore(endDate)) {
      // Add tick if the current interval overlaps with the visible range [startDate, endDate)
      const intervalEnd = currentDate.add(1, tickInterval);
      if (intervalEnd.isAfter(startDate) && currentDate.isBefore(endDate)) {
        if (!screens.md) {
          if (currentLevel == "day") {
            const dayOfMonth = currentDate.date();
            if (dayOfMonth % 4 != 0) {
              currentDate = currentDate.add(1, tickInterval);
              continue;
            }
          } else if (currentLevel == "month") {
            const month = currentDate.month();
            if (month % 2 != 0) {
              currentDate = currentDate.add(1, tickInterval);
              continue;
            }
          } else if (currentLevel == "year") {
            const year = currentDate.year();
            if (year % 2 != 0) {
              currentDate = currentDate.add(1, tickInterval);
              continue;
            }
          }
        }

        ticks.push({
          x: currentDate.toDate(), // Store as Date for scale compatibility
          y: 0,
          level: currentLevel,
        });
      }
      currentDate = currentDate.add(1, tickInterval);
    }

    // --- FIX 1 Applied Here ---
    // Check if the end date requires an additional marker tick
    // (e.g., for drawing the final vertical line)
    const lastTickDate = ticks[ticks.length - 1]?.x;
    if (lastTickDate) {
      const lastTickDayjs = dayjs(lastTickDate);
      const endOfRelevantUnit = lastTickDayjs.endOf(startUnit); // Use startUnit (day, month, year)

      // If the overall endDate extends beyond the end of the unit of the last tick drawn
      if (endDate.isAfter(endOfRelevantUnit)) {
        // Add a tick representing the *start* of the next interval/unit,
        // but only if it starts *before* the final endDate.
        const startOfNextUnit = lastTickDayjs
          .add(1, tickInterval)
          .startOf(startUnit);
        if (startOfNextUnit.isBefore(endDate)) {
          ticks.push({
            x: startOfNextUnit.toDate(),
            y: 0,
            level: currentLevel,
          });
        }
        // Optional: You might *also* want a tick precisely AT endDate
        // if your drawing logic requires a line exactly there.
        // Ensure it's not a duplicate of the last tick added.
        // if (!startOfNextUnit.isSame(endDate)){
        //   ticks.push({ x: endDate.toDate(), y: 0, level: currentLevel });
        // }
      }
    }

    return { tickData: ticks, level: currentLevel };
    // Depend on Dayjs objects and _days
  }, [dateMin, dateMax, width, _days, screens.md]);

  // --- Calculate Weekend Background Data ---
  const weekendRectsData = useMemo<WeekendRectData[]>(() => {
    // Use internal Dayjs objects (dateMin, dateMax)
    if (
      level !== "day" ||
      !dateMin.isValid() ||
      !dateMax.isValid() ||
      width <= 0 ||
      height <= 0
    ) {
      return [];
    }

    const startDate = dateMin; // Use Dayjs object
    const endDate = dateMax; // Use Dayjs object

    // Recreate scale here as it depends on the Date props
    const xScale = d3
      .scaleTime()
      .domain([dateMinProp, dateMaxProp])
      .range([0, width]);

    const ySeparatorLine = 32;
    const yBottom = height;
    const rectHeight = yBottom - ySeparatorLine;

    const weekendRects: WeekendRectData[] = [];
    let currentDate = startDate.startOf("day");

    while (currentDate.isBefore(endDate)) {
      const dayOfWeek = currentDate.day();

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        const dayStart = currentDate.startOf("day");
        const dayEnd = currentDate.endOf("day");

        // Check for overlap: day ends after view starts AND day starts before view ends
        if (dayEnd.isAfter(startDate) && dayStart.isBefore(endDate)) {
          // Clamp the rendering times to the view boundaries
          const visibleStart = dayStart.isBefore(startDate)
            ? startDate
            : dayStart;
          const visibleEnd = dayEnd.isAfter(endDate) ? endDate : dayEnd;

          try {
            // Convert back to Date ONLY for the scale function
            const startX = xScale(visibleStart.toDate());
            const endX = xScale(visibleEnd.toDate());
            const rectWidth = Math.max(0, endX - startX);

            if (rectWidth > 0.1) {
              // Use a small threshold to avoid zero-width rects
              weekendRects.push({
                id: `weekend-${currentDate.format("YYYYMMDD")}`,
                startX: startX,
                width: rectWidth,
                y: ySeparatorLine,
                height: rectHeight,
              });
            }
          } catch (e) {
            console.error("Error calculating weekend rect position:", e);
          }
        }
      }
      currentDate = currentDate.add(1, "day");
    }

    return weekendRects;
    // Depend on level, Dayjs dates, dimensions, and Date props for scale
  }, [level, dateMin, dateMax, width, height, dateMinProp, dateMaxProp]);

  // --- D3 Drawing Effect ---
  useEffect(() => {
    // Use Date props for scale domain
    if (
      !svgRef.current ||
      width <= 0 ||
      height <= 0 ||
      !dateMinProp ||
      !dateMaxProp
    ) {
      return;
    }

    const svg = d3.select(svgRef.current);
    const svgDates = svg.select<SVGGElement>("g.date");

    svgDates.selectAll("*").remove(); // Clear previous drawings

    // Recreate scale here within the effect scope
    const xScale = d3
      .scaleTime()
      .domain([dateMinProp, dateMaxProp])
      .range([0, width]);

    const yTop = 0;
    const yBottom = height;
    const ySeparatorLine = yTop + 32;
    const labelY = (yTop + ySeparatorLine) / 2;

    // --- Draw Date Header Background ---
    svgDates
      .append("rect")
      .attr("class", "date-header-background")
      .attr("x", 0)
      .attr("y", yTop)
      .attr("width", width)
      .attr("height", ySeparatorLine - yTop)
      .attr("fill", token.colorBgContainer)
      .attr("rx", 10);

    // --- Draw Weekend Backgrounds ---
    svgDates
      // *** FIX: Explicitly type the selection ***
      // Specify the Element type (SVGRectElement) and the Datum type (WeekendRectData)
      .selectAll<SVGRectElement, WeekendRectData>("rect.weekend-bg")
      // Now TypeScript should correctly infer 'd' as WeekendRectData in the key function
      .data(weekendRectsData, (d) => d.id) // Key function can likely remove the explicit type now, but keeping it is fine too.
      .enter()
      .append("rect")
      .attr("class", "weekend-bg")
      .attr("x", (d) => d.startX) // 'd' here is correctly inferred as WeekendRectData
      .attr("y", (d) => d.y)
      .attr("width", (d) => d.width)
      .attr("height", (d) => d.height)
      .attr("fill", token.colorBorder)
      .attr("opacity", 0.5)
      .lower();

    // --- Draw Bottom Border Line ---
    svgDates
      .append("line")
      .attr("class", "date-header-bottom-border")
      .attr("x1", 0)
      .attr("y1", ySeparatorLine)
      .attr("x2", width)
      .attr("y2", ySeparatorLine)
      .attr("stroke", token.colorBorder)
      .attr('stroke-width', token.lineHeight - 1)
      .attr("shape-rendering", "crispEdges");

    // --- Draw Vertical Tick Lines ---
    svgDates
      .selectAll("line.tick-separator")
      .data(tickData) // tickData still contains Date objects
      .enter()
      .append("line")
      .attr("class", "tick-separator")
      .attr("x1", (d) => xScale(d.x)) // xScale expects Date
      .attr("y1", ySeparatorLine)
      .attr("x2", (d) => xScale(d.x)) // xScale expects Date
      .attr("y2", yBottom)
      .attr("stroke", token.colorBorderSecondary)
      .attr("stroke-width", (d) => {
        if (d.level === "year") return 1.0;
        if (d.level === "month") return 0.5;
        return 0.3;
      })
      .attr("shape-rendering", "crispEdges");

    // --- Draw Date Labels ---
    svgDates
      .selectAll("text.tick-label")
      .data(tickData) // tickData still contains Date objects
      .enter()
      .filter((d) => {
        /* Optional filter logic */ return true;
      })
      .append("text")
      .attr("class", "tick-label")
      .attr("x", (d) => xScale(d.x)) // xScale expects Date
      .attr("y", labelY)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", token.colorTextTertiary)
      .attr("font-size", token.fontSizeSM)
      .text((d) => {
        const date = dayjs(d.x); // Convert Date back to Dayjs for formatting
        if (d.level === "year") {
          return date.format("YYYY");
        } else if (d.level === "month") {
          return date.format("MMM");
        } else {
          return date.format("D");
        }
      });

    // Effect Dependencies: Use props for scale, internal Dayjs for logic, calculated data
  }, [
    svgRef,
    dateMinProp,
    dateMaxProp,
    width,
    height,
    token,
    tickData,
    level,
    weekendRectsData,
  ]); // Use Date props for scale domain

  return null;
}
